import { CSSProperties } from 'react';
import iconImg from '../../assets/images/logo-tickarts-officiel-mini.svg';
import bannerImg from '../../assets/images/logo-tickarts-officiel.svg';
import { cn } from '../../utils/cn';

export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';
export type LogoVariant = 'icon' | 'banner';

interface LogoProps {
  variant?: LogoVariant;
  size?: LogoSize;
  className?: string;
  /**
   * Fond sombre ou coloré (rouge/noir). Le logo étant rouge + noir, il devient
   * illisible sur ces fonds : on le pose alors sur une pastille blanche.
   * Règle chromatique : JAMAIS de logo rouge/noir directement sur fond rouge ou noir.
   */
  onDark?: boolean;
}

const ICON_SIZES: Record<LogoSize, number> = {
  sm: 24,
  md: 32,
  lg: 48,
  xl: 72,
};

const BANNER_HEIGHTS: Record<LogoSize, number> = {
  sm: 28,
  md: 40,
  lg: 56,
  xl: 80,
};

export const Logo = ({
  variant = 'banner',
  size = 'md',
  className,
  onDark = false,
}: LogoProps) => {
  const isIcon = variant === 'icon';
  const px = isIcon ? ICON_SIZES[size] : undefined;
  const h = isIcon ? undefined : BANNER_HEIGHTS[size];

  const imgStyle: CSSProperties = isIcon
    ? { width: px, height: px, objectFit: 'contain' }
    : { height: h, width: 'auto', objectFit: 'contain' };

  const img = (
    <img
      src={isIcon ? iconImg : bannerImg}
      alt="TICK'ARTS"
      style={imgStyle}
      draggable={false}
      className={onDark ? undefined : cn(isIcon ? undefined : 'block', className)}
    />
  );

  // Sur fond sombre/coloré : pastille blanche pour garantir la lisibilité du
  // logo rouge/noir (aberration chromatique sinon).
  if (onDark) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center bg-white shadow-card',
          isIcon ? 'rounded-2xl p-2.5' : 'rounded-xl px-3 py-2',
          className,
        )}
      >
        {img}
      </span>
    );
  }

  return img;
};

export const LogoIcon = (props: Omit<LogoProps, 'variant'>) => <Logo {...props} variant="icon" />;
